/**
 * The garage turntable: one car, lit, on a plinth, that the player can spin.
 *
 * THE SAME RENDERER AS A MATCH. `createRenderer` from ./scene builds it, with
 * the same ACES tone mapping and the same exposure; the environment is the same
 * `makeEnvironmentTexture` the arena reflects; the car is `buildCar` with a real
 * `CarLivery`. Nothing about a car is drawn differently here, which is the whole
 * point — a preview that flatters an item is a preview that lies about it, and
 * the paint finishes in particular are only meaningful against the light rig
 * they will actually be seen under.
 *
 * What is NOT the same is the LIGHT RIG's geometry, and it cannot be: the
 * match's key light has a 2,200 uu shadow frustum aimed at a car somewhere in a
 * 10,000 uu arena, and pointing that at a stationary car sixty units across
 * spends the whole shadow map on empty pitch. The intensities, colours and
 * directions below are the match rig's (see `buildLighting` in ./scene); only
 * the distances and the frustum are a garage's.
 *
 * ONE CONTEXT AT A TIME. The renderer is built on `mount` and destroyed on
 * `unmount`, because a browser allows only a handful of WebGL contexts and the
 * match is entitled to one the moment the player leaves this screen.
 */

import {
  ACESFilmicToneMapping,
  AmbientLight,
  CircleGeometry,
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  Mesh,
  PCFSoftShadowMap,
  PMREMGenerator,
  PerspectiveCamera,
  Scene,
  ShadowMaterial,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { buildCar, type CarMesh } from './car-mesh';
import { CAR_ORIGIN_HEIGHT } from './car-body';
import { PALETTE, makeEnvironmentTexture } from './materials';
import type { CarLivery } from './livery';

/** Camera framing. The Dominus is 143 uu long, so this holds both bodies. */
const CAMERA_FOV = 30;
const CAMERA_DISTANCE = 268;
const CAMERA_HEIGHT = 82;
/** Where the camera looks: the middle of the car, a little above its floor. */
const CAMERA_TARGET = new Vector3(0, 10, 0);

/** Turntable speed with nobody touching it, radians per second. */
const IDLE_SPIN = 0.42;
/** Screen pixels to a full turn. */
const DRAG_PIXELS_PER_TURN = 520;
/** How fast a released drag falls back to the idle spin, 1/s. */
const SETTLE_RATE = 1.6;

export interface GaragePreviewOptions {
  /** Overridable for tests; defaults to `requestAnimationFrame`. */
  raf?: (cb: FrameRequestCallback) => number;
  cancel?: (handle: number) => void;
}

export class GaragePreview {
  private renderer: WebGLRenderer | null = null;
  private environment: Texture | null = null;
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(CAMERA_FOV, 1, 10, 4000);
  private readonly turntable = new Group();
  private car: CarMesh | null = null;
  private host: HTMLElement | null = null;

  private frame: number | null = null;
  private lastMs = 0;
  private yaw = -0.9;
  private spin = IDLE_SPIN;
  private dragging = false;
  private dragX = 0;
  /** The livery the built car is wearing, so an unchanged one is not rebuilt. */
  private built: { team: 0 | 1; livery: CarLivery } | null = null;

  private readonly raf: (cb: FrameRequestCallback) => number;
  private readonly cancelFrame: (handle: number) => void;
  private readonly onPointerDown: (event: PointerEvent) => void;
  private readonly onPointerMove: (event: PointerEvent) => void;
  private readonly onPointerUp: () => void;

  constructor(options: GaragePreviewOptions = {}) {
    this.raf = options.raf ?? ((cb) => requestAnimationFrame(cb));
    this.cancelFrame = options.cancel ?? ((h) => cancelAnimationFrame(h));

    this.scene.background = null;
    this.turntable.name = 'turntable';
    this.scene.add(this.turntable);

    // A plinth, so the car has something to cast onto.
    //
    // `ShadowMaterial` and not a surface: it draws the SHADOW and nothing else,
    // so the panel's own gradient shows through everywhere the car is not. A
    // real lit disc here is a large flat grey ellipse behind a dark menu, and it
    // reads as a plate the car is standing on rather than as a pool of light.
    const plinth = new Mesh(new CircleGeometry(110, 56), new ShadowMaterial({ opacity: 0.6 }));
    plinth.rotation.x = -Math.PI / 2;
    plinth.position.y = -CAR_ORIGIN_HEIGHT;
    plinth.receiveShadow = true;
    plinth.name = 'plinth';
    this.scene.add(plinth);

    this.scene.add(...buildPreviewLights());
    this.camera.position.set(0, CAMERA_HEIGHT, CAMERA_DISTANCE);
    this.camera.lookAt(CAMERA_TARGET);

    this.onPointerDown = (event) => {
      this.dragging = true;
      this.dragX = event.clientX;
      (event.target as Element | null)?.setPointerCapture?.(event.pointerId);
    };
    this.onPointerMove = (event) => {
      if (!this.dragging) return;
      const dx = event.clientX - this.dragX;
      this.dragX = event.clientX;
      this.yaw += (dx / DRAG_PIXELS_PER_TURN) * Math.PI * 2;
      // Throwing the car keeps it moving: the flick is the whole reason a
      // turntable is nicer than a pair of arrow buttons.
      this.spin = (dx / DRAG_PIXELS_PER_TURN) * Math.PI * 2 * 12;
    };
    this.onPointerUp = () => {
      this.dragging = false;
    };
  }

  /**
   * Put the preview inside `host` and start it.
   *
   * Idempotent for the same host, because `render()` in the overlay rebuilds the
   * panel markup whenever a row changes and would otherwise create a WebGL
   * context per keypress.
   */
  mount(host: HTMLElement): void {
    if (this.host === host && this.renderer !== null) return;
    this.detach();
    this.host = host;

    const renderer = new WebGLRenderer({ antialias: true, alpha: true, stencil: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = ACESFilmicToneMapping;
    // The match's exposure, lifted a little: there is no bright pitch under the
    // car here to carry the mid-tones, so at 0.98 a black car reads as a hole.
    renderer.toneMappingExposure = 1.12;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.touchAction = 'none';
    renderer.domElement.style.cursor = 'grab';
    host.appendChild(renderer.domElement);
    this.renderer = renderer;

    // The same procedural environment the arena reflects, prefiltered so rough
    // materials get a proper mip chain. This is what makes a paint finish mean
    // anything at all: with no environment term, gloss and matte are identical.
    const pmrem = new PMREMGenerator(renderer);
    const source = makeEnvironmentTexture();
    this.environment = pmrem.fromEquirectangular(source).texture;
    source.dispose();
    pmrem.dispose();
    this.scene.environment = this.environment;

    renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);

    this.lastMs = 0;
    this.tick(0);
  }

  /** Stop drawing and give the WebGL context back. */
  unmount(): void {
    this.detach();
  }

  /**
   * Build the car for a livery. A no-op when the livery has not changed, which
   * is what keeps a keypress from rebuilding a shell and a 1 MB texture.
   */
  setLivery(team: 0 | 1, livery: CarLivery): void {
    if (this.built !== null && this.built.team === team && sameLivery(this.built.livery, livery)) {
      return;
    }
    this.disposeCar();
    this.car = buildCar(team, livery.body, livery);
    this.turntable.add(this.car.group);
    this.built = { team, livery };
  }

  dispose(): void {
    this.detach();
    this.disposeCar();
  }

  // -------------------------------------------------------------------------

  private detach(): void {
    if (this.frame !== null) {
      this.cancelFrame(this.frame);
      this.frame = null;
    }
    const renderer = this.renderer;
    if (renderer !== null) {
      renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      renderer.dispose();
      // `dispose` frees the resources but leaves the context alive, and a
      // browser allows only a handful. The match wants one next.
      renderer.forceContextLoss();
      renderer.domElement.remove();
      this.renderer = null;
    }
    this.environment?.dispose();
    this.environment = null;
    this.scene.environment = null;
    this.host = null;
    this.dragging = false;
  }

  private disposeCar(): void {
    if (this.car === null) return;
    this.turntable.remove(this.car.group);
    this.car.dispose();
    this.car = null;
    this.built = null;
  }

  private tick(nowMs: number): void {
    const renderer = this.renderer;
    const host = this.host;
    if (renderer === null || host === null) return;

    const dt = this.lastMs === 0 ? 0 : Math.min(0.1, (nowMs - this.lastMs) / 1000);
    this.lastMs = nowMs;

    if (!this.dragging) {
      // Exponential settle back to the idle drift — the same 1 - exp(-k dt) law
      // the camera and the effects use, so it is frame-rate independent.
      this.spin += (IDLE_SPIN - this.spin) * (1 - Math.exp(-SETTLE_RATE * dt));
      this.yaw += this.spin * dt;
    }
    this.turntable.rotation.y = this.yaw;

    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    const size = renderer.getSize(SIZE_SCRATCH);
    if (Math.round(size.x) !== width || Math.round(size.y) !== height) {
      renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }

    renderer.render(this.scene, this.camera);
    this.frame = this.raf((next) => this.tick(next));
  }
}

/** One vector, reused: `getSize` allocates otherwise, sixty times a second. */
const SIZE_SCRATCH = new Vector2();

/**
 * The garage light rig.
 *
 * Intensities and colours are `buildLighting`'s in ./scene, unchanged, so a
 * metallic finish looks the same here as it does on the pitch. The POSITIONS
 * are a garage's — a car-sized shadow frustum instead of an arena-sized one —
 * and the rim is pushed further round because there is no goal frame behind the
 * car to separate it from.
 */
const buildPreviewLights = (): Group[] => {
  const group = new Group();
  group.name = 'garage-lighting';

  const key = new DirectionalLight(0xfff6ea, 2.7);
  key.position.set(150, 240, 190);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.near = 40;
  key.shadow.camera.far = 700;
  key.shadow.camera.left = -160;
  key.shadow.camera.right = 160;
  key.shadow.camera.top = 160;
  key.shadow.camera.bottom = -160;
  key.shadow.bias = -0.0008;
  key.shadow.normalBias = 1.5;
  group.add(key);

  const rim = new DirectionalLight(0xbcd8ff, 1.8);
  rim.position.set(-210, 90, -180);
  group.add(rim);

  const fill = new DirectionalLight(0x8fb4ff, 0.5);
  fill.position.set(-160, 120, 220);
  group.add(fill);

  group.add(new HemisphereLight(0x6d92d8, PALETTE.floor, 0.45));
  group.add(new AmbientLight(0xa8c4ff, 0.08));
  return [group];
};

/** Structural equality of the fields a built car depends on. */
const sameLivery = (a: CarLivery, b: CarLivery): boolean =>
  a.body === b.body &&
  a.team === b.team &&
  a.primary === b.primary &&
  a.accent === b.accent &&
  sameRef(a.finish, b.finish) &&
  sameRef(a.decal, b.decal) &&
  sameRef(a.number, b.number) &&
  sameRef(a.flag, b.flag) &&
  sameRef(a.wheels, b.wheels) &&
  sameRef(a.topper, b.topper) &&
  sameRef(a.antenna, b.antenna) &&
  sameRef(a.boost, b.boost) &&
  sameRef(a.trail, b.trail);

const sameRef = (a: CarLivery['decal'], b: CarLivery['decal']): boolean =>
  a === null || b === null ? a === b : a.itemId === b.itemId && a.paint === b.paint;

/** Exported for the test that asserts the preview never touches WebGL on import. */
export const previewBackground = (): Color => new Color(PALETTE.sky);
